<script lang="ts">
	import { clickoutside } from '@svelte-put/clickoutside';
	import { offset, flip, shift } from 'svelte-floating-ui/dom';
	import { createFloatingActions } from 'svelte-floating-ui';
	import RoundCheck from 'virtual:icons/ic/round-check';
	import { createEventDispatcher } from 'svelte';
	import { fly } from 'svelte/transition';
	import { twMerge } from 'tailwind-merge';

	interface Option {
		label: string;
		value: string;
	}

	const dispatch = createEventDispatcher();

	export let options: Option[] | string[] = [],
		selectedValue: string | null = null;

	let normalizedOptions: Option[] = [],
		selectedOption: Option | null = null,
		isShowPanel = false,
		focusedOptionIndex = -1,
		className = '';

	export { className as class };

	// Normalize options to {label, value}
	$: if (options.length) {
		normalizedOptions =
			typeof options[0] === 'string'
				? (options as string[]).map((opt) => ({ label: opt, value: opt }))
				: (options as Option[]);
	}

	// Sync selectedOption with the passed selectedValue
	$: selectedOption = normalizedOptions.find((option) => option.value === selectedValue) || null;

	function initializeFocusedIndex() {
		focusedOptionIndex = normalizedOptions.findIndex(
			(option) => option.value === selectedOption?.value
		);

		if (focusedOptionIndex === -1) focusedOptionIndex = 0;
	}

	function selectOption(option: Option) {
		selectedOption = option;
		selectedValue = option.value; // Update the bindable value
		isShowPanel = false;
		focusedOptionIndex = -1;

		dispatch('change', { value: option.value }); // Dispatch the selected value
	}

	function handleKeydown(event: KeyboardEvent) {
		switch (event.key) {
			case 'ArrowDown':
				if (isShowPanel) {
					if (focusedOptionIndex < normalizedOptions.length - 1) {
						focusedOptionIndex += 1;
					} else {
						focusedOptionIndex = 0;
					}
				} else {
					isShowPanel = true;
					initializeFocusedIndex();
				}
				break;
			case 'ArrowUp':
				if (isShowPanel) {
					if (focusedOptionIndex > 0) {
						focusedOptionIndex -= 1;
					} else {
						focusedOptionIndex = normalizedOptions.length - 1;
					}
				} else {
					isShowPanel = true;
					initializeFocusedIndex();
				}
				break;
			case 'Enter':
				if (
					isShowPanel &&
					focusedOptionIndex >= 0 &&
					focusedOptionIndex < normalizedOptions.length
				) {
					selectOption(normalizedOptions[focusedOptionIndex]);
				}
				break;
			case 'Escape':
				isShowPanel = false;
				focusedOptionIndex = -1;
				break;
		}
	}

	function handleClickOutside() {
		isShowPanel = false;
		focusedOptionIndex = -1;
	}

	function handleEnterKey(event: KeyboardEvent, option: Option) {
		if (event.key === 'Enter') selectOption(option);
	}

	function showPanel() {
		isShowPanel = !isShowPanel;
		if (isShowPanel) initializeFocusedIndex();
	}

	const [floatingRef, floatingContent] = createFloatingActions({
		strategy: 'absolute',
		placement: 'bottom',
		middleware: [flip(), shift({ crossAxis: true, padding: 20 }), offset(6)]
	});
</script>

<div
	class="relative {twMerge('min-w-52 max-w-52', className)}"
	use:clickoutside
	on:clickoutside={handleClickOutside}
>
	<button
		use:floatingRef
		type="button"
		class="select select-bordered flex w-full cursor-pointer items-center"
		on:click={showPanel}
		on:keydown|preventDefault={handleKeydown}
		aria-haspopup="listbox"
		aria-expanded={isShowPanel}
		aria-labelledby="select-label"
	>
		{selectedOption ? selectedOption.label : 'Pilih Salah Satu'}
	</button>

	{#if isShowPanel}
		<div
			transition:fly={{ duration: 350, y: -10 }}
			use:floatingContent
			role="listbox"
			class="z-10 grid w-full overflow-hidden rounded-lg border bg-base-100 p-2 shadow-lg"
			tabindex={-1}
		>
			{#each normalizedOptions as option, index}
				{@const isSelected = selectedOption?.value === option.value}
				{@const isFocused = focusedOptionIndex === index}
				<div
					role="option"
					class:selected={isSelected}
					class:focused={isFocused}
					class="flex cursor-pointer items-center justify-between gap-1.5 rounded-md p-2 hover:bg-gray-200"
					on:click={() => selectOption(option)}
					on:keydown|preventDefault={(event) => handleEnterKey(event, option)}
					tabindex={0}
					aria-selected={isSelected || isFocused}
				>
					<p class="line-clamp-1 flex-1 text-sm">{option.label}</p>
					{#if isSelected}
						<RoundCheck class="text-base text-primary" />
					{/if}
				</div>
			{/each}
		</div>
	{/if}
</div>

<style lang="postcss">
	[role='option'] {
		@apply cursor-pointer px-3 py-2 hover:bg-gray-200;

		&:is(.focused) {
			@apply bg-gray-200;
		}

		&:is(.selected) {
			@apply bg-primary/10;
		}
	}
</style>
